import {
  Box,
  BreadcrumbGroup,
  Grid,
  Icon,
  Tabs,
  Alert,
  Spinner,
} from '@amzn/awsui-components-react';
import { EuiCallOut, EuiSpacer } from '@elastic/eui';
import { get } from 'lodash';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
// @ts-ignore
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { ApiExplorer } from '../../Domain/ApiExplorer/ApiExplorer';
import { ClusterInsights } from '../../Domain/ClusterInsights/ClusterInsights';
import { DomainConfig } from '../../Domain/DomainConfig/DomainConfig';
import { UpgradeDetails } from '../../Domain/UpgradeDetails/UpgradeDetails';
import { Elb, ESEC2 } from '../../models/types';
import { AppContext } from '../../utils/appContext';
import { BREADCRUMBS } from '../../utils/constants';
import { getHealthyInstance } from '../../utils/helpers';
import { HawkEye } from './../../Domain/HawkEye/HawkEye';
import { DomainActions } from './DomainActions';
import { PerformanceAnalyser} from "../../Domain/PerformanceAnalyser/PerformanceAnalyser";
import {QueryInsights} from "../../Domain/QueryInsights/QueryInsights"
import {RTFMetrics} from "../../Domain/RTFMetrics/RTFMetrics"
import { DomainIdentifier } from './DomainIdentifier';
import { AgentHawkLabel } from './AgentHawkLabel';
import { useAgentHawkAvailability } from './hooks/useAgentHawkAvailability';
import { useResourceInfoQuery } from './hooks/useResourceInfoQuery';
import { Overview } from './Overview/Overview';
import { CrossClusterInsightsTab } from './CrossCluster/CrossClusterInsightsTab';
// @ts-ignore
import { DomainEvents } from '../../Domain/DomainEvents/DomainEvents';
import { KeyEvents } from '../../Domain/KeyEvents/KeyEvents';
import { Logs } from '../../Domain/Logs/Logs';
import '../../Styles/domain-details.css';
import { SdpdsClusterDetails } from './SDPDS';
import { SkewAnalysis } from './StorageConsole/SkewAnalysis/SkewAnalysis';
import { SnapshotsInfo } from './StorageConsole/SnapshotsInfo/SnapshotsInfo';

import { ErrorBoundary } from 'react-error-boundary';
import { Fallback } from "../../Components/Fallback";
import { RavenChat } from '../../Domain/RavenChat/RavenChat';
import {isError} from "../../ErrorHandler/apiErrorHandler";
import { refreshData } from "./utils/refreshData";
import { deriveErrorMessage } from "./utils/deriveErrorMessage";
import { useDomainListQuery } from "../AccountOverview/hooks/useDomainListsQuery";
import { ALERTS } from "./utils/constants";
import { getInitialAppContext } from '../../utils/appContext';
import { useToastNotifications } from '../../hooks/useToastList';
import { fetchDomainMetricsAggregations, fetchIndexesAggregations, fetchNodeMultiMetricData, fetchResourceRelatedMetrics, fetchShardsAggregations, Filters } from '../../Domain/ClusterInsights/Hooks/useGetMetricsAggregationsQuery';
import { useApolloClient } from '@apollo/client';
import { useDomainIdentuple } from '../../Domain/ClusterInsights/Hooks/useDomainIdentuple';
import { TimeRange, ClusterResourceSummary, commonConstants } from '@amzn/search-services-cluster-insights-commons-lib';
import { CommandExplorer } from '../../Domain/CommandExplorer/CommandExplorer';
import { DEFAULT_FILTERS, DEFAULT_SORTING_METRIC, NODES_DEFAULT_SORTING_METRIC } from '../../constants';
import { useClusterInfoQuery } from './hooks/useClusterInfoQuery';
import { useExecuteFilteredDataApiMutation } from '../../hooks/useExecuteFilteredDataApiMutation';
import { RenderClusterResourceSummary } from './renderClusterInsightsRootComponents';
import { apiCall } from '../../sdpds/utils';

interface DetailsProps {
  domainIdentifier: string;
  authUser: boolean;
}

interface DomainDetailsParams {
  clientId: string;
  domainName: string;
}

const DomainDetails = (props: DetailsProps) => {
  const params = useParams();
  const tab = params['tab'];
  const appContext = useContext(AppContext);
  // Stage/region allowlist plus the BFF health check; the check is shared with
  // the other Agent Hawk entry points so it only fires once per page load.
  const {
    loading: agentHawkLoading,
    available: agentHawkAvailable,
    enabledForStageRegion: isAgentHawkEnabled,
  } = useAgentHawkAvailability();
  const showAgentHawk = isAgentHawkEnabled && (agentHawkAvailable || agentHawkLoading);
  const [executeFilteredApi, { loading }] = useExecuteFilteredDataApiMutation();
  const client = useApolloClient();
  const { domainName, clientId } = useParams<DomainDetailsParams>();
  const { pathname, hash } = useLocation();
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [domainVersion, setDomainVersion] = useState('0.0.0');
  const toastNotification = useToastNotifications()

  const { loading: domainListLoading, data: domainListData, error: domainListError } = useDomainListQuery({ clientId });

  const {
    loading: resourceLoading,
    data,
    error,
    refetch,
  } = useResourceInfoQuery({
    domainIdentifier: props.domainIdentifier,
  });

  const isErrorPresent = isError(error)
  const isDomainListErrorPresent = isError(domainListError)
  const domainIdentuple = useDomainIdentuple(props.domainIdentifier);

  const fetchDataNodes = useCallback(
    (_http: unknown, _domainIdent: string, range: TimeRange, filters: Filters, sortingField = NODES_DEFAULT_SORTING_METRIC) => {
        return fetchDomainMetricsAggregations(client, domainIdentuple, range, filters, sortingField, executeFilteredApi)
      },
    [client, domainIdentuple, data]
  );
  const fetchIndexes = useCallback(
    (_http: unknown, _domainIdent: string, range: TimeRange, filters: Filters, sortingField = DEFAULT_SORTING_METRIC) => {
        return fetchIndexesAggregations(client, domainIdentuple, range, filters, sortingField)
      },
    [client, domainIdentuple]
  );
  const fetchShards = useCallback(
    (_http: unknown, _domainIdent: string, range: TimeRange, filters: Filters, sortingField = DEFAULT_SORTING_METRIC) => {
        return fetchShardsAggregations(client, domainIdentuple, range, filters, sortingField)
      },
    [client, domainIdentuple]
  );

  const fetchResourceRelatedMetricsHandler = useCallback(
    (_http: unknown, _domainIdent: string, id: string, range: TimeRange, filters: Filters, metricsList: string[], keyMap: Record<string, string>, isShardMetrics: boolean) => {
        return fetchResourceRelatedMetrics(
          client,
          domainIdentuple,
          id,
          range,
          filters,
          metricsList,
          keyMap,
          isShardMetrics,
        )
      },
    [client, domainIdentuple]
  );

  const fetchNodeMultiMetricDataHandler = useCallback(
    (_http: unknown, _domainName: string, metricNames: string[], id: string, range: TimeRange, filters = DEFAULT_FILTERS, keyMap: Record<string, string>, isShardMetrics: boolean = false) => {
        return fetchNodeMultiMetricData(
          client,
          domainIdentuple,
          metricNames,
          id,
          range,
          filters,
          keyMap,
          isShardMetrics,
        )
      },
    [client, domainIdentuple]
  );

  useEffect(() => {
   if ( isErrorPresent) {
      setErrorMessage(deriveErrorMessage(error, 'domainDetails'));
    } else {
       // Query is healthy: drop any banner left over from an earlier failure.
       setErrorMessage(null);
    }
  }, [isErrorPresent, error]);
  const handleRefreshData = async () => {
    await refreshData(refetch, setIsFetching, setErrorMessage, isFetching, 'domainDetails');
  };

  const ec2Instances: ESEC2[] = get(
    data,
    'domain.resources.ALL.ec2Instances',
    []
  );
  const esVersion: string = get(data, 'domain.esVersion', undefined);
  const cellId: string = get(data, 'domain.cellId', undefined);
  const HAProxyVersion: string = get(data, 'domain.HAProxyVersion', undefined);
  const HAProxyType: string = get(data, 'domain.HAProxyType', undefined);
  const pinnedVFIDomain: string = get(
    data,
    'domain.pinnedVFIDomain',
    undefined
  );
  const pinnedVFIClient: string = get(
    data,
    'domain.pinnedVFIClient',
    undefined
  );
  const IsSensitiveDomain: string = get(
    data,
    'domain.IsSensitiveDomain',
    undefined
  );
  const IsSensitiveClient: string = get(
    data,
    'domain.IsSensitiveClient',
    undefined
  );
  const IsDomainIsolated: string = get(
    data,
    'domain.IsDomainIsolated',
    undefined
  );

  const PackageTypeAssociatedOnDomain: string = get(
    data,
    'domain.PackageTypeAssociatedOnDomain',
    undefined
  );

  const carbonDomain: string = get(data, 'domain.carbonDomain', undefined);
  const elb: Elb = get(data, 'domain.resources.ALL.elb', {
    targetGroupName: 'string',
    loadBalancerName: '',
  });
  const DomainCreateDateTime: string = get(
      data,
      'domain.DomainCreateDateTime',
      undefined
  );

  let isDomainUnknownOrDeleted = false
  //TODO need to fix Network error on firefox
  if (!(domainListError && domainListError.message.includes("NetworkError when attempting to fetch resource"))) {
    const domainList = get(domainListData, 'account.domains', []);
    const domainExists = domainList.some(domain => (domain.domainIdentifier === props.domainIdentifier));
    isDomainUnknownOrDeleted = !domainListLoading && !domainExists;
  }

  const isShareMetricRoute = hash ? hash.indexOf('shared-metric') > -1 : false;

  const healthyInstance = getHealthyInstance(ec2Instances);

  const dragonstoneDomain = get(data, 'domain.dragonstoneDomain', undefined);

  const isMustangDomain = get(data, 'domain.isMustangDomain', false);
  
  const IsRemotePublicationEnabled = get(data, 'domain.IsRemotePublicationEnabled', undefined)

  console.log("is remote publication", IsRemotePublicationEnabled)

  // const awsAccountId = "915766840868"
  // const awsAccountId = props.domainIdentifier.split(":")[0]
  // console.log(awsAccountId)
  // const { loading, data: OtsDomainData, error: OtsDomainError } = useOtsItemsQuery({ awsAccountId });
  // const { loading, data: OtsDomainData, error: OtsDomainError } = useOtsAllQuery();
  // const otsAll = get(OtsDomainData, 'otsAll.otsItems', []);

  // let isOtsDomain = otsAll.find(record => record["account_id"] === awsAccountId)
  // console.log("domainFound")
  // console.log(isOtsDomain)

  // if (isOtsDomain) {
  //   console.log("It is an OTS Domain")
  // }

  useEffect(() => {
    appContext.setBreadcrumbs([
      {
        ...BREADCRUMBS.TUMBLER,
        href: '/',
        onClick: () => {
          history.push('/');
        },
      },
      {
        text: clientId,
        href: `/${clientId}`,
        onClick: () => {
          history.push(`/${clientId}`);
        },
      },
      { text: domainName, href: `/${clientId}/${domainName}` },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  var currentTab;

  if (tab) {
    currentTab = tab;
  } else {
    currentTab = 'overview';
    var newPath = pathname;
    if (pathname.slice(-1) === '/') {
      newPath = pathname.slice(0, -1);
    }
    history.replace({ pathname: newPath + `/overview` });
  }

  const [activeTab, setActiveTab] = useState(currentTab);

  const handleTabChange = useCallback((selectedTab) => {
    var refresh =
      pathname.split('/').slice(0, -1).join('/') + `/${selectedTab}`;
    history.push({ pathname: `${refresh}` });
    setActiveTab(selectedTab);
  }, [pathname, history]);

  useEffect(() => {
    if (activeTab === 'agenthawk' && !agentHawkLoading && !agentHawkAvailable) {
      handleTabChange('overview');
    }
  }, [activeTab, agentHawkLoading, agentHawkAvailable, handleTabChange]);

  const renderTabs = () => (
    <Tabs
      tabs={tabs}
      activeTabId={activeTab}
      onChange={({ detail }) => handleTabChange(detail.activeTabId)}
    />
  );

  const tabs = [
    ...(showAgentHawk ? [{
      id: 'agenthawk',
      label: <AgentHawkLabel />,
      route: 'agenthawk',
      content: null, // Rendered via early return in full-page mode
    }] : []),
    {
      id: 'overview',
      label: 'Overview',
      route: 'overview',
      content: (
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"OverviewTab"}/>}>
        <Overview
          domainIdentifier={props.domainIdentifier}
          resourceLoading={resourceLoading}
          ec2Instances={ec2Instances}
          elb={elb}
          esVersion={esVersion || 'N/A'}
          cellId={cellId || (appContext.rootAccountId || 'N/A')}
          HAProxyVersion={HAProxyVersion || 'N/A'}
          HAProxyType={HAProxyType || 'N/A'}
          pinnedVFIDomain={pinnedVFIDomain || 'N/A'}
          pinnedVFIClient={pinnedVFIClient || 'N/A'}
          IsSensitiveDomain={IsSensitiveDomain || 'N/A'}
          IsSensitiveClient={IsSensitiveClient || 'N/A'}
          IsDomainIsolated={IsDomainIsolated || 'N/A'}
          carbonDomain={carbonDomain || 'N/A'}
          domainData={data}
          refreshResources={refetch}
          errorMessage={errorMessage}
          handleRefreshData={handleRefreshData}
          isFetching={isFetching}
          dragonstoneDomain={dragonstoneDomain}
          isMustangDomain={isMustangDomain}
          IsRemotePublicationEnabled={IsRemotePublicationEnabled}
          DomainCreateDateTime={DomainCreateDateTime || 'N/A'}
          PackageTypeAssociatedOnDomain={PackageTypeAssociatedOnDomain || 'N/A'}
          IsIndexLevelEncryptionEnabled={get(data, 'domain.IsIndexLevelEncryptionEnabled', false)}
          DeploymentStrategy={get(data, 'domain.DeploymentStrategy', undefined) || 'N/A'}
          UpgradeDeploymentStrategy={get(data, 'domain.UpgradeDeploymentStrategy', undefined) || 'N/A'}
        />
        </ErrorBoundary>
      ),
    },
    {
      id: 'crossClusterInsights',
      label: 'Cross-Cluster Insights',
      route: 'cross-cluster-insights',
      content:
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"CrossClusterInsightsTab"}/>}>
          <CrossClusterInsightsTab domainIdentifier={props.domainIdentifier} />
        </ErrorBoundary>,
    },
    {
      id: 'logs',
      label: 'Logs',
      route: 'logs',
      content:
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"LogsTab"}/>}>
          <Logs 
              authUser = {props.authUser}
          />
        </ErrorBoundary>,
    },
    {
      id: 'clusterInsights',
      label: 'Cluster Insights',
      route: 'clusterInsights',
      disabled: appContext?.region?.partition !== 'aws',
      content:
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"ClusterInsightsTab"}/>}>
          <ClusterInsights
            domainIdentifier={props.domainIdentifier}
          />
        </ErrorBoundary>,
    },
    {
      id: 'nodeInsights',
      label: 'Node Insights',
      route: 'nodeInsights',
      disabled: appContext?.region?.partition !== 'aws',
      content:
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"NodeInsightsTab"}/>}>
          <RenderClusterResourceSummary
            domainIdentuple={domainIdentuple}
            resourceLoading={resourceLoading}
            domainVersion={domainVersion}
            message="Cluster insights is not supported for domains below OS 2.17"
          >
            <ClusterResourceSummary
              notifications={{ toasts: toastNotification }}
              http={{}}
              title="Data Nodes Summary"
              domainName={props.domainIdentifier}
              entityField="nodeId"
              fetchEntities={fetchDataNodes}
              fetchMetrics={fetchResourceRelatedMetricsHandler}
              metrics={commonConstants.NODE_METRICS}
              metricsFilterKeyMap={commonConstants.NODE_METRICS_FILTER_KEY_NODE_MAP}
              unitMap={commonConstants.NODE_METRICS_HEADER_UNIT_MAP}
              defaultSort={DEFAULT_SORTING_METRIC}
              fetchNodeMultiMetricData={fetchNodeMultiMetricDataHandler}
              showTopShardsColumn={true}
              isTumblerView={true}
              handleTabChange={handleTabChange}
            />
          </RenderClusterResourceSummary>
        </ErrorBoundary>,
    },
    {
      id: 'indexInsights',
      label: 'Index Insights',
      route: 'indexInsights',
      disabled: appContext?.region?.partition !== 'aws',
      content:
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"IndexInsightsTab"}/>}>
          <RenderClusterResourceSummary
            domainIdentuple={domainIdentuple}
            resourceLoading={resourceLoading}
            domainVersion={domainVersion}
            addVfiCheck={true}
            message="Cluster insights is not supported for domains below OS 2.17"
          >
            <ClusterResourceSummary
              notifications={{ toasts: toastNotification }}
              http={{}}
              title="Indexes"
              domainName={props.domainIdentifier}
              entityField="indexName"
              fetchEntities={fetchIndexes}
              fetchMetrics={fetchResourceRelatedMetricsHandler}
              metrics={commonConstants.INDEX_METRICS}
              metricsFilterKeyMap={commonConstants.INDEX_METRICS_FILTER_KEY_NODE_MAP}
              unitMap={commonConstants.INDEX_METRICS_HEADER_UNIT_MAP}
              defaultSort={DEFAULT_SORTING_METRIC}
              fetchNodeMultiMetricData={fetchNodeMultiMetricDataHandler}
              showTopShardsColumn={true}
              isTumblerView={true}
              handleTabChange={handleTabChange}
            />
          </RenderClusterResourceSummary>
        </ErrorBoundary>,
    },
    {
      id: 'shardInsights',
      label: 'Shard Insights',
      route: 'shardInsights',
      disabled: appContext?.region?.partition !== 'aws',
      content:
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"ShardInsightsTab"}/>}>
          <RenderClusterResourceSummary
            domainIdentuple={domainIdentuple}
            resourceLoading={resourceLoading}
            domainVersion={domainVersion}
            addVfiCheck={true}
            message="Cluster insights is not supported for domains below OS 2.17"
          >
            <ClusterResourceSummary
              notifications={{ toasts: toastNotification }}
              http={{}}
              title="Shards"
              domainName={props.domainIdentifier}
              entityField="shardId"
              fetchEntities={fetchShards}
              fetchMetrics={fetchResourceRelatedMetricsHandler}
              metrics={commonConstants.SHARD_METRICS}
              metricsFilterKeyMap={commonConstants.SHARD_METRICS_FILTER_KEY_NODE_MAP}
              unitMap={commonConstants.SHARD_METRICS_HEADER_UNIT_MAP}
              defaultSort={DEFAULT_SORTING_METRIC}
              fetchNodeMultiMetricData={fetchNodeMultiMetricDataHandler}
            />
          </RenderClusterResourceSummary>
        </ErrorBoundary>,
    },
    {
      id: 'apiExplored',
      label: 'API explorer',
      route: 'explorer',
      content: (
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"APIExplorerTab"}/>}>
        <ApiExplorer
          domainIdentifier={props.domainIdentifier}
          instanceId={healthyInstance ? healthyInstance.instanceId : ''}
          esVersion={esVersion || 'N/A'}
          nodeCount={ec2Instances.length}
        />
        </ErrorBoundary>
      ),
    },
    {
      id: 'commandExplorer',
	    label: 'Command explorer',
	    route: 'commandexplorer',
      content: (
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"CommandExplorerTab"}/>}>
          <CommandExplorer
            domainIdentifier={props.domainIdentifier}
            instances={ec2Instances}
          />
        </ErrorBoundary>
      ),
      disabled: true
    },
    {
      id: 'configurations',
      label: 'Configurations',
      route: 'configurations',
      content:
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"ConfigurationsTab"}/>}>
          <DomainConfig domainIdentifier={props.domainIdentifier} />
        </ErrorBoundary>,
    },
    {
      id: 'snapshots',
      label: 'Snapshots',
      route: 'snapshots',
      content: (
        // <ContentPanel title={'Snapshots Info'}>
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"SnapshotsTab"}/>}>
        <SnapshotsInfo
          domainIdentifier={props.domainIdentifier}
          resourceLoading={resourceLoading}
          domainData={data}
        />
        </ErrorBoundary>
        // </ContentPanel>
      ),
    },
    {
      id: 'skewAnalysis',
      label: 'Skew analysis',
      route: 'skew-analysis',
      content: (
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"SkewAnalysisTab"}/>}>
        <SkewAnalysis
          domainIdentifier={props.domainIdentifier}
          resourceLoading={resourceLoading}
          domainData={data}
        />
        </ErrorBoundary>
      ),
    },
    {
      id: 'upgradeHistory',
      label: 'Upgrade history',
      route: 'upgrade-history',
      content: (
        // <ContentPanel title={'Upgrade details'}>
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"UpgradeHistoryTab"}/>}>
        <UpgradeDetails domainIdentifier={props.domainIdentifier} />
        </ErrorBoundary>
        // </ContentPanel>
      ),
    },
    {
      id: 'keyEvents',
      label: 'Key Events',
      route: 'key-events',
      content:
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"KeyEventTab"}/>}>
          <KeyEvents domainIdentifier={props.domainIdentifier} />
        </ErrorBoundary>,
    },
    {
      id: 'domainEvents',
      label: 'Domain Events',
      route: 'domain-events',
      content:
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"DomainEventsTab"}/>}>
          <DomainEvents domainIdentifier={props.domainIdentifier} />
        </ErrorBoundary>,
    },
    {
      id: 'sdpds',
      label: 'SDPDS',
      route: 'sdpds',
      content: (
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"SDPDSTab"}/>}>
        <SdpdsClusterDetails domainIdentifier={props.domainIdentifier} />
        </ErrorBoundary>
      ),
    },
    {
      id: 'queryInsights',
      label: 'Query Insights',
      route: 'query-insights',
      content: (
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"QueryInsightsTab"}/>}>
          <QueryInsights
            domainIdentifier={props.domainIdentifier}
            authUser={props.authUser}
            instanceId={healthyInstance ? healthyInstance.instanceId : ''}
          />
        </ErrorBoundary>
      ),
    },
    {
      id: 'rtfMetrics',
      label: 'RTF Metrics',
      route: 'rtf-metrics',
      content: (
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"RTFMetricsTab"}/>}>
          <RTFMetrics
            domainIdentifier={props.domainIdentifier}
            authUser={props.authUser}
            ec2Instances={ec2Instances}
            esVersion={esVersion || 'N/A'}
          />
        </ErrorBoundary>
      ),
    },
    {
      id: 'performanceAnalyser',
      label: 'Performance Analyser',
      route: 'performance-analyser',
      content: (
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"PerformanceAnalyserTab"}/>}>
          <PerformanceAnalyser
              domainIdentifier={props.domainIdentifier}
              authUser = {props.authUser}
              instanceId={healthyInstance ? healthyInstance.instanceId : ''}
          />
        </ErrorBoundary>
      ),
    },
    {
      id: 'hawkeye',
      label: 'Hawk Eye (Preview)',
      route: 'hawkeye',
      content:
        <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"HawkEyeTab"}/>}>
          <HawkEye domainIdentifier={props.domainIdentifier} />
        </ErrorBoundary>,
    },
  ];

  useEffect(() => {
    if (!resourceLoading && data?.domain?.esVersion) {
      setDomainVersion(data?.domain?.esVersion?.split('_')?.[1] || '0.0.0')
    }
  }, [resourceLoading])

  if (activeTab === 'agenthawk' && showAgentHawk) {
    if (agentHawkLoading) {
      return <Spinner size="large" />;
    }
    return (
      <ErrorBoundary fallbackRender={props => <Fallback error={props.error} failedComponent={"RavenChatTab"}/>}>
        <RavenChat domainIdentifier={props.domainIdentifier} cellId={cellId} onBackToOverview={() => handleTabChange('overview')} onTabChange={handleTabChange} />
      </ErrorBoundary>
    );
  }

  return (
    <React.Fragment>
      {/* {isOtsDomain && <Flashbar className="otsBanner" items={[
        {
          type: "warning",
          content: "This is OTS domain",
        }
      ]} />} */}
      <Box className="topMessage" variant="div">
        <Icon name="status-info" size="normal" variant="normal" /> &nbsp; We are
        aware of{' '}
        <a
          href="https://w.amazon.com/bin/view/AWS/SearchServices/OpsTumbler/Release#HActivelyWorkingOn"
          target="_blank"
          rel="noopener noreferrer"
        >
          these
        </a>{' '}
        bugs/issues on which team is currently working on!
      </Box>
      {ALERTS.map((alert, index) => (
          <Alert
              key={index}
              header={alert.header}
              type={alert.type}
              statusIconAriaLabel={alert.statusIconAriaLabel}
              dismissible={alert.dismissible}
          >
            {alert.content}
          </Alert>
      ))}

      <BreadcrumbGroup items={appContext.breadcrumbs} />
      {isDomainListErrorPresent ? (
        <div>
          <EuiCallOut
            title={
              <span>
                <b>Something went wrong</b>
              </span>
            }
            color="danger"
            iconType="alert"
            size="m"
          >
            <p>
              Please refesh the page or try again later
            </p>
          </EuiCallOut>
          <EuiSpacer />
        </div>
      ) : (isDomainUnknownOrDeleted ? (
        <div>
          <EuiCallOut
            title={
              <span>
                Sorry, domain identifier <b>{props.domainIdentifier}</b> is
                either <b>deleted</b> or <b>doesn't exist</b>
              </span>
            }
            color="danger"
            iconType="alert"
            size="m"
          >
            <p>
              You may navigate to domain list for AccountId{' '}
              <Link to={`/${clientId}`}>{clientId}</Link> or switch to correct
              region from region switcher.
            </p>
          </EuiCallOut>
          <EuiSpacer />
        </div>
      ): null)}

      <Grid
        gridDefinition={[
          { colspan: { default: 1, xxs: 11 } },
          { colspan: { default: 11, xxs: 1 } },
        ]}
      >
        <DomainIdentifier domainIdentifier={props.domainIdentifier} />
        <DomainActions
          domainIdentifier={props.domainIdentifier}
          ec2Instances={[]}
        />
      </Grid>

      {isShareMetricRoute || !domainIdentuple ? null : renderTabs()}
    </React.Fragment>
  );
};
export { DomainDetails };
